import React, { useState, useEffect, useRef } from 'react';

/**
 * PhoneCallPopup - Reusable popup for both Outgoing and Incoming calls.
 *
 * Props:
 *   - device: The global Twilio Device instance (from App.js)
 *   - callType: 'outgoing' | 'incoming'
 *   - incomingCall: The Twilio Call object (only for incoming calls)
 *   - patientName: Display name
 */
const PhoneCallPopup = ({ device, callType, incomingCall, patientName, phoneNumber, appointmentId, patientId, providerId, onClose }) => {
  const [activeCall, setActiveCall] = useState(null);

  // Call States: 'connecting', 'ringing', 'in-progress', 'no-answer', 'incoming'
  const [callState, setCallState] = useState(
    callType === 'incoming' ? 'incoming' : 'connecting'
  );

  // Timer State
  const [callDuration, setCallDuration] = useState(0);
  const timerRef = useRef(null);

  // Timer Logic: Starts ticking when call is picked up (in-progress)
  useEffect(() => {
    if (callState === 'in-progress') {
      console.log('POPUP: Call is in-progress. Starting timer.');
      timerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [callState]);

  // Tell EditSchedule that the call ended when this popup closes
  useEffect(() => {
    return () => {
      window.dispatchEvent(new CustomEvent('end-outgoing-call'));
    };
  }, []);

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Attach event listeners to a call object
  const attachCallListeners = (call) => {
    console.log('POPUP: Attaching listeners to call object...');

    call.on('ringing', () => {
      console.log('POPUP EVENT: call is ringing');
      setCallState('ringing');
    });

    call.on('accept', () => {
      console.log('POPUP EVENT: call accepted');
      setCallState('in-progress');
    });

    call.on('disconnect', () => {
      console.log('POPUP EVENT: call disconnected. Current callState:', callState);
      setCallState((prevState) => {
        if (prevState === 'connecting' || prevState === 'ringing') {
          setTimeout(() => onClose(), 2000);
          return 'no-answer';
        }
        onClose();
        return prevState;
      });
    });

    call.on('cancel', () => {
      console.log('POPUP EVENT: call cancelled');
      setCallState('no-answer');
      setTimeout(() => onClose(), 2000);
    });

    call.on('reject', () => {
      console.log('POPUP EVENT: call rejected');
      setCallState('no-answer');
      setTimeout(() => onClose(), 2000);
    });

    call.on('error', (error) => {
      console.error('POPUP EVENT: Call Error:', error);
      onClose();
    });
  };

  // For OUTGOING calls: initiate the call using the global device
  useEffect(() => {
    console.log(`POPUP: Running outgoing call effect. callType=${callType}, device exists=${!!device}`);
    if (callType !== 'outgoing') return;

    if (!device) {
      console.error('POPUP: No Twilio device provided to PhoneCallPopup');
      return;
    }

    if (device.state === 'destroyed') {
      console.error('POPUP: Twilio device is destroyed! Cannot make call.');
      return;
    }

    console.log('POPUP: Making outgoing call! Device state:', device.state, 'Phone Number:', phoneNumber);

    const makeCall = async () => {
      try {
        console.log('POPUP: Calling device.connect()...');
        // console.log('POPUP: Sending params: ', {
        //   patientPhoneNumber: phoneNumber,
        //   Direction: 'outbound',
        //   appointment_id: String(appointmentId || ''),
        //   patient_id: String(patientId || ''),
        //   provider_id: String(providerId || '')
        // });
        // return;
        const call = await device.connect({
          params: {
            patientPhoneNumber: phoneNumber,
            Direction: 'outbound',
            appointment_id: String(appointmentId || ''),
            patient_id: String(patientId || ''),
            provider_id: String(providerId || '')
          }
        });
        console.log('POPUP: device.connect() returned call object:', call);
        setActiveCall(call);
        attachCallListeners(call);
      } catch (err) {
        console.error('POPUP: Error making outgoing call:', err);
        onClose();
      }
    };

    makeCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callType, device]);

  // For INCOMING calls: store the call and attach listeners
  useEffect(() => {
    if (callType !== 'incoming' || !incomingCall) return;

    setActiveCall(incomingCall);
    attachCallListeners(incomingCall);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callType, incomingCall]);

  // Handle Accept (Incoming only)
  const handleAcceptCall = () => {
    if (activeCall) {
      activeCall.accept();
      setCallState('in-progress');
    }
  };

  // Handle Decline / Cut Call
  const handleEndCall = () => {
    console.log('POPUP: End Call button clicked.');
    if (activeCall) {
      if (callState === 'incoming') {
        console.log('POPUP: Rejecting incoming call');
        activeCall.reject();
      } else {
        console.log('POPUP: Disconnecting active call');
        activeCall.disconnect();
      }
    } else {
      console.log('POPUP: No activeCall object found, just closing popup.');
    }
    onClose();
  };

  console.log('POPUP: Rendering UI... current callState:', callState);

  return (
    <div style={{
      position: 'fixed',
      top: '100px',
      right: '20px',
      backgroundColor: 'white',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
      borderRadius: '8px',
      padding: '24px',
      width: '320px',
      zIndex: 9999,
      textAlign: 'center',
      border: '1px solid #eaeaea'
    }}>
      {/* Dynamic Status Text / Timer */}
      <p style={{ fontSize: '13px', color: '#888', textAlign: 'right', margin: 0, fontWeight: '500' }}>
        {callState === 'incoming' && <span style={{ color: '#3b82f6' }}>Incoming Calling...</span>}
        {callState === 'connecting' && 'Connecting...'}
        {callState === 'ringing' && <span style={{ color: '#f59e0b' }}>Ringing...</span>}
        {callState === 'in-progress' && <span style={{ color: '#10b981' }}>In Call • {formatTime(callDuration)}</span>}
        {callState === 'no-answer' && <span style={{ color: '#ef4444' }}>Call Unanswered</span>}
      </p>

      <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '16px', marginBottom: '8px', color: '#333' }}>
        {patientName || 'Unknown Caller'}
      </h3>
      <p style={{ color: '#666', marginBottom: '24px', fontSize: '14px' }}>
        {phoneNumber}
      </p>

      {/* Action Buttons */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
        {callState !== 'no-answer' && (
          <button
            onClick={handleEndCall}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              flex: 1
            }}
          >
            {callState === 'incoming' ? 'Decline' : 'Cut Call'}
          </button>
        )}

        {/* Accept button only for incoming calls that haven't been answered yet */}
        {callState === 'incoming' && (
          <button
            onClick={handleAcceptCall}
            style={{
              backgroundColor: '#22c55e',
              color: 'white',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              flex: 1
            }}
          >
            Accept
          </button>
        )}
      </div>
    </div>
  );
};

export default PhoneCallPopup;
